import { Box, Typography, useTheme, Chip, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import analyticsService, { UserActivityWithDetails } from '../../../../../services/analytics/AnalyticsService';
import { ActivityTypeColors } from '../../../../../config/constants';
import moment from 'moment';
import PortalCard from '../../../../../components/PortalCard';

const RecentActivityCard: React.FC = () => {
    const theme = useTheme();
    const [activities, setActivities] = useState<UserActivityWithDetails[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRecentActivity = async () => {
            try {
                const result = await analyticsService.getRecentActivities(25);
                if (result.success && result.data) {
                    setActivities(result.data);
                }
            } catch (error) {
                console.error('Error fetching recent activities:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchRecentActivity();
    }, []);

    const formatActivityType = (type: string): string => {
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getActivityTypeColor = (type: string): string => {
        return ActivityTypeColors[type as keyof typeof ActivityTypeColors] || '#9E9E9E';
    };

    return (
        <PortalCard
            sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* Fixed Header */}
            <Box sx={{ p: 3, pb: 2 }}>
                <Typography variant='h6' fontWeight='bold' fontFamily='primaryFont'>
                    Recent Activity
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    {`Last ${activities.length} user interactions`}
                </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 3,
                    pb: 3,
                    '&::-webkit-scrollbar': {
                        width: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: 3,
                    }
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : activities.length === 0 ? (
                    <Typography variant='body2' color='text.secondary' fontFamily='primaryFont' sx={{ py: 2, textAlign: 'center' }}>
                        No recent activity
                    </Typography>
                ) : (
                    <List dense>
                        {activities.map((activity, index) => (
                            <ListItem
                                key={activity.id}
                                sx={{
                                    px: 0,
                                    borderBottom: index < activities.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Chip
                                                label={formatActivityType(activity.activityType)}
                                                size='small'
                                                sx={{
                                                    backgroundColor: getActivityTypeColor(activity.activityType),
                                                    color: '#FFFFFF',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.65rem',
                                                }}
                                            />
                                            <Typography variant='body2' fontFamily='primaryFont'>
                                                {activity.screenName || 'Unknown'}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                            {activity.anonymousId?.slice(0, 8).toUpperCase() || 'Unknown'} • {moment(activity.created).fromNow()}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </PortalCard>
    );
};

export default RecentActivityCard;

